import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Container } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from "react-helmet";
import Main from '../../layout/main';
import OtpInputNumber from "../../components/inputs/otp-input";
import Buttons from "../../components/buttons/button-primary";
import ButtonsSecondary from "../../components/buttons/button-secondary";
import { registerOtp, clearRegisterOtpError } from '../../redux/actions/register-otp';
import { registerResendOtp, clearRegisterResendOtpMessages } from '../../redux/actions/registrationResendOtp';
import { toastr } from 'react-redux-toastr';
import Cookies from 'js-cookie';
import { isLogin } from '../../utils';
import axios from 'axios';
import API from '../../API';

const OtpVerification = () => {
    const dispatch = useDispatch();
    
    // Redux selectors
    const registerOtpData = useSelector(state => state.registerOtp);
    const registerResendOtpData = useSelector(state => state.registerResendOtp);
    const authData = useSelector(state => state.auth);
    
    const [state, setState] = useState({
        otp: "",
        otpError: "",
    });

    const [shouldClearError, setShouldClearError] = useState(false);

    const handleOtpChange = (otp) => {
        setState(prevState => ({
            ...prevState,
            otp: otp,
            otpError: "",
        }));
        
        // Clear Redux errors when user starts typing
        if (registerOtpData.error) {
            dispatch(clearRegisterOtpError());
        }
        if (registerResendOtpData.error || registerResendOtpData.success) {
            dispatch(clearRegisterResendOtpMessages());
        }
    };

    const validateOtp = () => {
        if (!state.otp || state.otp.trim() === '') {
            setState(prevState => ({
                ...prevState,
                otpError: "Please enter the OTP"
            }));
            return false;
        }
        if (state.otp.length < 6) {
            setState(prevState => ({
                ...prevState,
                otpError: "Please enter 6 digit OTP"
            }));
            return false;
        }
        return true;
    };

    const handleVerifyOtp = () => {
        // Clear error flag when submitting
        setShouldClearError(false);
        
        if (!validateOtp()) {
            return;
        }

        // Clear any existing errors
        dispatch(clearRegisterOtpError());
        dispatch(clearRegisterResendOtpMessages());
        
        // Clear local OTP error if validation passes
        setState(prevState => ({
            ...prevState,
            otpError: ""
        }));

        // Use the existing Redux action for OTP verification
        const confirmationToken = Cookies.get('confirmation_token');
        console.log('OTP Component - Using confirmation_token for verification:', confirmationToken);
        
        const data = JSON.stringify({
            confirmation_token: confirmationToken,
            otp: state.otp
        });

        dispatch(registerOtp(data));
    };

    const handleResendOtp = async () => {
        // Clear all errors before making the API call
        dispatch(clearRegisterOtpError());
        dispatch(clearRegisterResendOtpMessages());
        
        // Clear OTP input
        setState(prevState => ({
            ...prevState,
            otp: "",
        }));

        // Use the existing Redux action for resend OTP
        
        const confirmationToken = await Cookies.get('confirmation_token');

        dispatch(registerResendOtp(confirmationToken));
    };

    const handleBackToLogin = () => {
        // Clear auth cookies and redirect to login
        Cookies.remove('isLogin');
        Cookies.remove("authentication_token");
        Cookies.remove('access_token');
        Cookies.remove('uid');
        Cookies.remove('client');
        Cookies.remove('provider_id');
        Cookies.remove('email');
        Cookies.remove('mobileNumber');
        
        window.location.href = '/login';
    };

    // Handle successful OTP verification
    useEffect(() => {
        console.log('OTP Success useEffect - registerOtpData:', registerOtpData);
        console.log('OTP Success useEffect - registration:', registerOtpData.registration);
        console.log('OTP Success useEffect - completedRegsteps:', registerOtpData.completedRegsteps);
        console.log('OTP Success useEffect - authData:', authData);
        
        // Check if OTP verification was successful
        // The API response structure shows: {code: 200, data: {otp_verified: true, ...}}
        if (registerOtpData.registration && 
            registerOtpData.registration.code === 200 && 
            registerOtpData.registration.data && 
            registerOtpData.registration.data.otp_verified === true) {
            console.log('OTP Success useEffect - OTP verification successful, redirecting to dashboard');
            // Clear the email cookie as it's no longer needed
            Cookies.remove('email');
            toastr.success('Success', 'OTP verified successfully!');
            // Redirect to dashboard after successful verification
            window.location.href = '/dashboard';
        }
        
        // Fallback: Check if auth data is set and user is logged in
        if (authData && authData.auth && authData.auth.authentication_token && isLogin()) {
            console.log('OTP Success useEffect - Auth data present, redirecting to dashboard');
            // Clear the email cookie as it's no longer needed
            Cookies.remove('email');
            toastr.success('Success', 'OTP verified successfully!');
            // Redirect to dashboard after successful verification
            window.location.href = '/dashboard';
        }
    }, [registerOtpData.registration, authData]);

    // Handle successful resend OTP
    useEffect(() => {
        if (registerResendOtpData.success) {
            toastr.success('Success', 'OTP sent successfully!');
        }
    }, [registerResendOtpData.success]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });

        // Check if user has the necessary authentication data
        const authToken = Cookies.get('authentication_token');
        const userEmail = Cookies.get('email');
        const userUid = Cookies.get('uid');
        
        console.log('OTP Component - Auth Token (confirmation_token):', authToken);
        console.log('OTP Component - User Email:', userEmail);
        console.log('OTP Component - User UID:', userUid);
        console.log('OTP Component - isLogin():', isLogin());
        console.log('OTP Component - All cookies:', document.cookie);
        
        // For OTP verification, we only need the authentication_token
        // The email/uid might not be set yet, but that's okay for OTP verification
        if (!authToken) {
            // User doesn't have authentication token, redirect to login
            console.log('OTP Component - Missing auth token, redirecting to login');
            toastr.error('Error', 'Please login again to verify your OTP.');
            window.location.href = '/login';
        } else {
            console.log('OTP Component - Auth token present, showing OTP form');
            
            // Call test API to get OTP details using confirmation token
            console.log('OTP Component - Calling get_user_details API with confirmation_token:', authToken);
            axios.get(API.baseURL + `/get_user_details?mobile_number=${userEmail}`, API.options)
                .then(response => {
                    console.log('OTP Component - get_user_details API response:', response.data);
                    // You can see the OTP details in the network tab and console
                })
                .catch(error => {
                    console.log('OTP Component - get_user_details API call failed:', error);
                });
        }
    }, []);

    return (
        <div>
            <Helmet>
                <meta charSet="utf-8" />
                <title>EasyBids - OTP Verification</title>
                <link rel="stylesheet" href="//cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"></link>
            </Helmet>
            <Main>
                <section className="ebw-section__pages">
                    <Container>
                        <Row>
                            <Col sm="12" md="10" lg="6" className="mx-auto text-center">
                                <div className="ebw-entry__content-rgt position-relative" style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e5e5',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    padding: '30px',
                                    borderRadius: '8px',
                                    height: 'auto'
                                }}>
                                    <div className="ebw-entry__register-flow position-relative d-flex flex-column mx-auto justify-content-center">
                                        <h2 className="ebw-entry__title">
                                            <div className="ebw-entry__title-top">OTP Verification</div>
                                            <div className="ebw-entry__title-bottom mb-3">
                                                Please verify your OTP to continue
                                            </div>
                                        </h2>
                                        
                                        <div className="text-left mb-4">
                                            <p className="text-muted">
                                                We've sent a verification code to your email address. 
                                                Please enter the 6-digit code below to verify your account.
                                            </p>
                                        </div>

                                        <Form className="ebw-entry__tab-form">
                                            <Row form>
                                                <Col sm="12">
                                                    <OtpInputNumber
                                                        value={state.otp}
                                                        changeEvent={handleOtpChange}
                                                        otpError={state.otpError}
                                                    />
                                                    
                                                    {registerOtpData.error && (
                                                        <div className="text-left mb-3" style={{ color: "var(--danger)", opacity: "0.9" }}>
                                                            <div className="ebw-error">{registerOtpData.error}</div>
                                                        </div>
                                                    )}
                                                    
                                                    {registerResendOtpData.error && (
                                                        <div className="text-left mb-3" style={{ color: "var(--danger)", opacity: "0.9" }}>
                                                            <div className="ebw-error">{registerResendOtpData.error}</div>
                                                        </div>
                                                    )}
                                                    
                                                    {registerResendOtpData.success && (
                                                        <div className="text-left mb-3" style={{ color: "var(--success)", opacity: "0.9" }}>
                                                            <div className="ebw-success">{registerResendOtpData.success}</div>
                                                        </div>
                                                    )}
                                                </Col>

                                                <Col md="6" lg="8" xl="6" className="mt-5 mx-auto">
                                                    <div className="d-flex justify-content-between">
                                                        <div className="me-3">
                                                            <ButtonsSecondary 
                                                                clickEvent={handleBackToLogin} 
                                                                type="button" 
                                                                text="Back to Login" 
                                                            />
                                                        </div>
                                                        <div>
                                                            <Buttons 
                                                                clickEvent={handleVerifyOtp} 
                                                                type="button" 
                                                                text={registerOtpData.loading ? "Verifying..." : "Verify OTP"} 
                                                                disabled={registerOtpData.loading}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col sm="12" className="mt-4">
                                                    <div className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link text-decoration-none"
                                                            onClick={handleResendOtp}
                                                            disabled={registerResendOtpData.loading}
                                                            style={{ color: "var(--primary)" }}
                                                        >
                                                            {registerResendOtpData.loading ? "Sending..." : "Resend OTP"}
                                                        </button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </Main>
        </div>
    );
};

export default OtpVerification;